import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { format, subMonths } from 'date-fns'
import { ArrowLeft, Printer } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { computeOwedByTutor } from '../lib/payout'
import { formatMoney } from '../lib/pricing'
import { formatRate } from '../lib/rates'
import {
  PayoutSessionRow, OwedPeriod, CustomRange, OWED_PERIOD_LABELS, filterToPeriod, fetchAllPayoutSessions,
} from '../lib/payoutQueries'

type TutorLite = { id: string; name: string; email: string }

function minutesToHuman(min: number): string {
  const h = Math.floor(min / 60)
  const r = min % 60
  if (h === 0) return `${r}m`
  return r === 0 ? `${h}h` : `${h}h ${r}m`
}

/** 'yyyy-MM-dd' → formatted label, or null when missing/malformed (hand-edited URL). */
function formatDay(day: string | null | undefined): string | null {
  if (!day) return null
  const d = new Date(day + 'T00:00:00')
  return isNaN(d.getTime()) ? null : format(d, 'MMM d, yyyy')
}

function periodDates(period: OwedPeriod, custom: CustomRange): string {
  const now = new Date()
  if (period === 'month') return format(now, 'MMMM yyyy')
  if (period === 'lastMonth') return format(subMonths(now, 1), 'MMMM yyyy')
  if (period === 'custom') {
    const from = formatDay(custom.from)
    const to = formatDay(custom.to)
    if (from && to) return `${from} – ${to}`
    if (from) return `From ${from}`
    if (to) return `Through ${to}`
    return 'All dates'
  }
  return `Through ${format(now, 'MMM d, yyyy')}`
}

/**
 * Printable payout statement: how much is owed to every tutor for the
 * selected period. Standalone route (no dashboard chrome) so the browser's
 * Print → Save as PDF produces a clean document. Admin-only — money.
 */
export function TutorPayoutInvoice() {
  const [searchParams] = useSearchParams()
  const raw = searchParams.get('period')
  const period: OwedPeriod =
    raw === 'lastMonth' || raw === 'all' || raw === 'month' || raw === 'custom' ? raw : 'month'
  const custom: CustomRange = {
    from: searchParams.get('from') || null,
    to: searchParams.get('to') || null,
  }

  const [loading, setLoading] = useState(true)
  const [tutors, setTutors] = useState<TutorLite[]>([])
  const [sessions, setSessions] = useState<PayoutSessionRow[]>([])
  const [overrides, setOverrides] = useState<Map<string, number | null>>(new Map())
  const [ratesByTutor, setRatesByTutor] = useState<Map<string, number | null>>(new Map())

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [{ data: t }, { data: billing }, { data: sb }, sessRows] = await Promise.all([
          supabase.from('tutors').select('id, name, email').order('name'),
          supabase.from('tutor_billing').select('tutor_id, hourly_rate'),
          supabase.from('student_billing').select('student_id, hourly_rate_override'),
          fetchAllPayoutSessions(),
        ])
        if (cancelled) return
        setTutors((t as TutorLite[]) || [])
        setRatesByTutor(new Map((billing || []).map((b) => [b.tutor_id, b.hourly_rate])))
        setOverrides(new Map((sb || []).map((r) => [r.student_id, r.hourly_rate_override])))
        setSessions(sessRows)
      } catch (e) {
        console.error('Error loading payout invoice:', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const inPeriod = useMemo(
    () => filterToPeriod(sessions, period, custom),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions, period, custom.from, custom.to]
  )
  const owedByTutor = useMemo(
    () => computeOwedByTutor(inPeriod, overrides, ratesByTutor),
    [inPeriod, overrides, ratesByTutor]
  )

  const rows = useMemo(() => {
    return tutors
      .map((t) => {
        const owed = owedByTutor.get(t.id)
        if (!owed || owed.payableCount === 0) return null
        const hasOverride = inPeriod.some(
          (s) => s.tutor_id === t.id && (overrides.get(s.student_id) ?? null) !== null
        )
        return { tutor: t, owed, hasOverride, rateMissing: owed.missingRateCount === owed.payableCount }
      })
      .filter((r): r is NonNullable<typeof r> => r !== null)
      .sort((a, b) => b.owed.total - a.owed.total)
  }, [tutors, owedByTutor, inPeriod, overrides])

  const grandTotal = rows.reduce((sum, r) => sum + r.owed.total, 0)
  const anyOverride = rows.some((r) => r.hasOverride)
  const anyMissingRate = rows.some((r) => r.owed.missingRateCount > 0)
  const now = new Date()
  const statementNo = `MT-${format(now, 'yyyyMMdd')}-${period.toUpperCase()}`

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-surface-0 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-surface-0 py-8 px-4 print:bg-white print:p-0">
      <style>{`@page { margin: 14mm; } @media print { html, body { background: #fff !important; } }`}</style>

      {/* Toolbar — never printed */}
      <div className="max-w-3xl mx-auto mb-5 flex items-center justify-between print:hidden">
        <Link to="/dashboard/tutors"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400">
          <ArrowLeft className="h-4 w-4" /> Back to Tutors
        </Link>
        <button onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 shadow-sm">
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>

      {/* The statement — deliberately paper-white in both themes */}
      <div className="max-w-3xl mx-auto bg-white text-gray-900 rounded-xl shadow-xl print:shadow-none print:rounded-none p-10 sm:p-12 print:p-0">
        {/* Letterhead */}
        <div className="flex items-start justify-between gap-6 pb-6 border-b-2 border-gray-900">
          <div>
            <p className="text-2xl font-bold tracking-tight">MyTutor<span className="text-brand-600">+</span></p>
            <p className="mt-1 text-xs text-gray-500">Tutoring management</p>
          </div>
          <div className="text-right">
            <h1 className="text-lg font-semibold uppercase tracking-[0.2em] text-gray-900">Payout statement</h1>
            <p className="mt-1 text-xs text-gray-500 tabular-nums">{statementNo}</p>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 py-6 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-400">Period</p>
            <p className="mt-0.5 font-medium">{OWED_PERIOD_LABELS[period]}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-400">Dates</p>
            <p className="mt-0.5 font-medium tabular-nums">{periodDates(period, custom)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-400">Generated</p>
            <p className="mt-0.5 font-medium tabular-nums">{format(now, 'MMM d, yyyy · h:mm a')}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-gray-400">Total due</p>
            <p className="mt-0.5 font-semibold tabular-nums">{formatMoney(grandTotal)}</p>
          </div>
        </div>

        {/* Table */}
        {rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-500">
            No payable sessions in this period — nothing is owed.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-300 text-[11px] uppercase tracking-wider text-gray-400">
                <th className="py-2 text-left font-medium">Tutor</th>
                <th className="py-2 text-right font-medium">Sessions</th>
                <th className="py-2 text-right font-medium">Hours</th>
                <th className="py-2 text-right font-medium">Rate</th>
                <th className="py-2 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ tutor, owed, hasOverride, rateMissing }) => (
                <tr key={tutor.id} className="border-b border-gray-100 break-inside-avoid">
                  <td className="py-2.5 pr-4">
                    <p className="font-medium">{tutor.name}</p>
                    <p className="text-xs text-gray-500">{tutor.email}</p>
                  </td>
                  <td className="py-2.5 text-right tabular-nums align-top">{owed.payableCount}</td>
                  <td className="py-2.5 text-right tabular-nums align-top whitespace-nowrap">{minutesToHuman(owed.payableMinutes)}</td>
                  <td className="py-2.5 text-right tabular-nums align-top whitespace-nowrap">
                    {formatRate(ratesByTutor.get(tutor.id))}{hasOverride ? ' †' : ''}
                  </td>
                  <td className="py-2.5 pl-4 text-right font-semibold tabular-nums align-top whitespace-nowrap">
                    {rateMissing ? '— *' : formatMoney(owed.total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-900">
                <td colSpan={4} className="pt-3 text-right text-[11px] uppercase tracking-wider text-gray-400">
                  Total due · {OWED_PERIOD_LABELS[period]}
                </td>
                <td className="pt-3 pl-4 text-right text-base font-bold tabular-nums whitespace-nowrap">
                  {formatMoney(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}

        {/* Footnotes */}
        <div className="mt-8 pt-4 border-t border-gray-100 text-[11px] leading-relaxed text-gray-400">
          <p>Amounts are approved, non-cancelled session hours × hourly rate for the period shown.</p>
          {anyOverride && <p>† Includes per-student rate overrides where set.</p>}
          {anyMissingRate && <p>* Tutor has sessions without an hourly rate set — those sessions are excluded from the total.</p>}
          <p className="mt-2">Generated by MyTutor+ · {format(now, 'MMM d, yyyy')}</p>
        </div>
      </div>
    </div>
  )
}
